import {
  backButton,
  hapticFeedback,
  init,
  isTMA,
  miniApp,
  retrieveRawInitData,
  swipeBehavior,
  viewport,
} from '@telegram-apps/sdk-react';

const INK = '#0a0a0b';

export interface TelegramBoot {
  initData: string | null;
  startParam: string | null;
  inTelegram: boolean;
}

/** Re-apply safe areas as CSS vars: device notch + Telegram's own header/bottom bar. */
function bindSafeAreas() {
  const apply = () => {
    const sa = viewport.safeAreaInsets();
    const csa = viewport.contentSafeAreaInsets();
    const root = document.documentElement.style;
    root.setProperty('--safe-top', `${(sa?.top ?? 0) + (csa?.top ?? 0)}px`);
    root.setProperty('--safe-bottom', `${(sa?.bottom ?? 0) + (csa?.bottom ?? 0)}px`);
  };
  apply();
  viewport.safeAreaInsets.sub(apply);
  viewport.contentSafeAreaInsets.sub(apply);
}

/**
 * Boot the Telegram SDK. Outside Telegram (plain browser) everything degrades to no-ops;
 * with VITE_TG_MOCK=1 `mock.ts` has already faked the environment before this runs.
 */
export async function bootTelegram(): Promise<TelegramBoot> {
  if (!(await isTMA('complete'))) return { initData: null, startParam: null, inTelegram: false };
  init();
  try {
    if (miniApp.mount.isAvailable()) {
      miniApp.mount();
      miniApp.setHeaderColor.ifAvailable(INK);
      miniApp.setBackgroundColor.ifAvailable(INK);
      miniApp.setBottomBarColor.ifAvailable(INK);
    }
    if (viewport.mount.isAvailable()) {
      // Never block first paint on the viewport handshake: safe areas apply when it lands.
      void viewport
        .mount()
        .then(() => {
          viewport.expand.ifAvailable();
          bindSafeAreas();
        })
        .catch(() => {});
    }
    if (swipeBehavior.mount.isAvailable()) {
      swipeBehavior.mount();
      swipeBehavior.disableVertical.ifAvailable(); // the wheel and lists scroll vertically
    }
    if (backButton.mount.isAvailable()) backButton.mount();
  } catch {
    /* older clients: run without the optional components */
  }
  let initData: string | null;
  try {
    initData = retrieveRawInitData() ?? null;
  } catch {
    initData = null;
  }
  const startParam = initData ? new URLSearchParams(initData).get('start_param') : null;
  return { initData, startParam, inTelegram: true };
}

export function markReady() {
  miniApp.ready.ifAvailable();
}

/** DESIGN §3 haptics: light taps, success on award, selection ticks. All no-ops outside Telegram. */
export const haptic = {
  tap: () => hapticFeedback.impactOccurred.ifAvailable('light'),
  success: () => hapticFeedback.notificationOccurred.ifAvailable('success'),
  error: () => hapticFeedback.notificationOccurred.ifAvailable('error'),
  tick: () => hapticFeedback.selectionChanged.ifAvailable(),
};

/**
 * Native back button as a stack: a sheet opened over Profile gets Back first, and the
 * button hides only when nothing is left to close. Returns a cleanup.
 */
const backStack: (() => void)[] = [];
let backListening = false;
export function bindNativeBack(onBack: (() => void) | null) {
  if (!onBack) return () => {};
  if (!backListening) {
    const on = backButton.onClick.ifAvailable(() => backStack.at(-1)?.());
    backListening = Boolean(on[0]);
  }
  backStack.push(onBack);
  backButton.show.ifAvailable();
  return () => {
    const i = backStack.lastIndexOf(onBack);
    if (i >= 0) backStack.splice(i, 1);
    if (!backStack.length) backButton.hide.ifAvailable();
  };
}
